// Org-side platform contract review page — /settings/contract (Settings area,
// org-level: reachable with zero facilities via ORG_LEVEL_PREFIXES).
//
// Read model: getMyOrg → owner-safe active terms and version/acceptance history —
// the GENERATED ContractTermsResponse (OWNER-only on the backend; a 403 renders the
// honest owner-only state, not a crash). Write model: POST
// /orgs/{id}/accept-contract-terms with the currently read version id, exact
// document hash, and retry-stable command UUID. The backend re-resolves the
// ACTIVE version and rejects drift. That server-side binding pins the UI:
//
//   * The Accept affordance exists ONLY when requires_reacceptance === true
//     (the active version is unaccepted). A not-yet-effective upcoming version
//     can NEVER be pre-accepted — POSTing would re-accept the CURRENT version
//     while looking like it accepted the upcoming one — so the upcoming
//     section is informational only (version number + effective date).
//   * There is NO withdraw/un-accept affordance in any state — DELETE
//     accept-contract-terms raises InvalidStatusError for approved/active/
//     suspended orgs (withdrawal is an onboarding-only affordance owned by
//     onboarding's ContractTermsPanel).
//   * past_grandfather === true means the previous acceptance's 60-day grace
//     window has CLOSED: TransactionService refuses all charges
//     ("contract_terms_not_accepted") until the current terms are accepted.
//     The page says so honestly — acceptance required AND payments blocked.
//
// Accept is gated by a real confirm dialog (role=alertdialog). Rendering is
// never optimistic: after a successful POST the page refetches
// safe terms/history and onboarding state, and renders the SERVER's accepted_at.
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { isApiError } from "@bw/shared/api";
import { formatMoney, formatRateFraction } from "@bw/shared/types";
import { useMyOrg } from "@/hooks/use-my-org";
import { useOwnerContract } from "./useOwnerContract";
import { OwnerFeeTerms } from "./OwnerFeeTerms";
import { OwnerContractHistory } from "./OwnerContractHistory";
import { ExactAgreementDocument } from "./ExactAgreementDocument";
import {
  acceptContractTerms,
  orgId as readOrgId,
  type ContractTerms,
} from "@/libs/onboardingApi";

// Human-friendly date for DATE-ONLY wire values ("2026-01-01"). Parsed at local
// midnight so the rendered day can't shift across the UTC boundary. Absent or
// unparseable → an honest "—", never "Invalid Date".
function formatDateOnly(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Human-friendly date for TIMESTAMP wire values ("2026-01-15T12:00:00Z").
function formatTimestampDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Version label: the human version NUMBER ("v2"). UUIDs never render.
function versionLabel(versionNumber: number | null | undefined): string {
  return versionNumber != null ? `v${versionNumber}` : "—";
}

// A grouped key/value fact row (matches the org-settings Row treatment).
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "minmax(8rem, auto) minmax(0, 1fr)",
        py: 1.25,
      }}
    >
      <Typography color="text.secondary" component="dt" variant="body2">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ fontWeight: 700, m: 0, minWidth: 0, textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TermsSection({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) {
  return (
    <Paper component="section" variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>{children}</Box>
    </Paper>
  );
}

export function ContractPage() {
  const queryClient = useQueryClient();
  const view = useMyOrg();
  const org = view.org;
  const id = org ? readOrgId(org) : undefined;
  const ownerContract = useOwnerContract(id);
  const stateQuery = ownerContract.termsQuery;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const acceptanceKey = useRef(globalThis.crypto.randomUUID());

  const acceptMutation = useMutation({
    mutationFn: (input: { versionId: string; documentSha256: string }) =>
      acceptContractTerms(id as string, {
        expected_version_id: input.versionId,
        expected_document_sha256: input.documentSha256,
      }, acceptanceKey.current),
    onSuccess: async () => {
      // Never optimistic: refetch safe terms/history and render the SERVER's new
      // accepted_at / requires_reacceptance.
      await queryClient.invalidateQueries({
        queryKey: ["org", id, "onboarding-state"],
      });
      await Promise.all([
        ownerContract.refresh(),
        queryClient.invalidateQueries({ queryKey: ["org", "me"] }),
      ]);
      acceptanceKey.current = globalThis.crypto.randomUUID();
      setConfirmOpen(false);
    },
    onError: () => setConfirmOpen(false),
  });

  if (view.kind === "loading" || (id && stateQuery.isPending)) {
    return (
      <Stack
        direction="row"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
          px: { xs: 2, lg: 3 },
        }}
      >
        <CircularProgress size={18} />
        <Typography color="text.secondary" variant="body2">
          Loading contract terms…
        </Typography>
      </Stack>
    );
  }

  if (view.kind === "error") {
    return (
      <Alert severity="error" sx={{ mx: { xs: 2, lg: 3 } }}>
        Could not load your organization. Try again later.
      </Alert>
    );
  }

  if (!org || !id) {
    return (
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ px: { xs: 2, lg: 3 }, py: 3 }}
      >
        No organization found for your account.
      </Typography>
    );
  }

  if (stateQuery.isError) {
    if (isApiError(stateQuery.error) && stateQuery.error.status === 403) {
      // The safe terms read is owner-only on the backend. Honest state
      // for staff/managers — no terms, no actions, no crash.
      return (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ maxWidth: 448, px: { xs: 2, lg: 3 }, py: 3 }}
        >
          Only the organization owner can view and accept the platform contract.
        </Typography>
      );
    }
    return (
      <Alert severity="error" sx={{ mx: { xs: 2, lg: 3 } }}>
        Could not load the contract terms. Try again later.
      </Alert>
    );
  }

  const terms: ContractTerms | null = stateQuery.data ?? null;

  if (!terms) {
    return (
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ px: { xs: 2, lg: 3 }, py: 3 }}
      >
        No platform contract has been published for your organization yet.
      </Typography>
    );
  }

  const requiresReacceptance = terms.requires_reacceptance === true;
  const canAcceptExactAgreement = Boolean(
    requiresReacceptance && terms.version_id && terms.document,
  );
  const pastGrandfather = terms.past_grandfather === true;
  const hasUpcoming =
    terms.upcoming_version_number != null ||
    terms.upcoming_effective_date != null;
  const feeDisplay = formatRateFraction(terms.commission_rate, 2).replace(
    /\.?0+%$/,
    "%",
  );

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 4 }}>
      {pastGrandfather ? (
        <Alert
          action={canAcceptExactAgreement ? (
            <Button
              color="inherit"
              disabled={acceptMutation.isPending}
              onClick={() => setConfirmOpen(true)}
              size="small"
              variant="outlined"
            >
              Accept current agreement terms
            </Button>
          ) : undefined}
          severity="error"
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Acceptance required — payments are blocked
            </Typography>
            <Typography variant="body2">
              The grace period for the previous terms has ended. Your facilities
              cannot take payments or charges until you accept the current
              terms.
            </Typography>
          </Stack>
        </Alert>
      ) : requiresReacceptance ? (
        <Alert
          action={canAcceptExactAgreement ? (
            <Button
              color="inherit"
              disabled={acceptMutation.isPending}
              onClick={() => setConfirmOpen(true)}
              size="small"
              variant="outlined"
            >
              Accept current agreement terms
            </Button>
          ) : undefined}
          severity="warning"
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              The platform terms have changed
            </Typography>
            <Typography variant="body2">
              Review the current terms below and accept them to keep your
              economic terms up to date.
            </Typography>
          </Stack>
        </Alert>
      ) : null}

      {acceptMutation.isError ? (
        <Alert severity="error">
          Could not record your acceptance. Please try again.
        </Alert>
      ) : null}

      <TermsSection title="Current terms">
        <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
          {!terms.service_fee_terms?.length ? (
            <Row label="Commission rate" value={feeDisplay} />
          ) : null}
          <Row
            label="Payout frequency"
            value={
              <Box component="span" sx={{ textTransform: "capitalize" }}>
                {terms.payout_schedule?.frequency ?? "—"}
              </Box>
            }
          />
          <Row
            label="Minimum payout"
            value={formatMoney(terms.payout_schedule?.min_payout_amount)}
          />
          <Row label="Version" value={versionLabel(terms.version_number)} />
          <Row
            label="Effective date"
            value={formatDateOnly(terms.effective_date)}
          />
          <Row
            label="Accepted"
            value={formatTimestampDate(terms.accepted_at)}
          />
          {terms.termination_date ? (
            <Typography color="warning.main" variant="body2" sx={{ pt: 1.25 }}>
              These terms terminate on {formatDateOnly(terms.termination_date)}.
            </Typography>
          ) : null}
        </Stack>
        <OwnerFeeTerms terms={terms} />
      </TermsSection>

      <ExactAgreementDocument
        document={terms.document}
        unavailableCopy="This version has economic terms but no exact reviewed agreement document. It cannot be accepted as a legal agreement."
      />
      <Button size="small" variant="outlined" component={Link} to="/commercial/offers">Review module offers</Button>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setShowHistory(!showHistory)}
      >
        {showHistory ? "Hide contract history" : "View contract history"}
      </Button>
      {showHistory ? (
        ownerContract.versionsQuery.isError ||
        ownerContract.acceptancesQuery.isError ? (
          <Alert severity="warning">
            Contract history unavailable. Refresh to retry.
          </Alert>
        ) : ownerContract.versionsQuery.isPending ||
          ownerContract.acceptancesQuery.isPending ? (
          <Typography role="status">Loading contract history…</Typography>
        ) : (
          <OwnerContractHistory
            versions={ownerContract.versionsQuery.data ?? []}
            acceptances={ownerContract.acceptancesQuery.data ?? []}
          />
        )
      ) : null}

      {hasUpcoming ? (
        <TermsSection title="Upcoming version">
          <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
            <Row
              label="Version"
              value={versionLabel(terms.upcoming_version_number)}
            />
            <Row
              label="Takes effect"
              value={formatDateOnly(terms.upcoming_effective_date)}
            />
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ pt: 1.25 }}
            >
              For review only — you will be asked to accept this version once it
              becomes active.
            </Typography>
          </Stack>
        </TermsSection>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!acceptMutation.isPending) setConfirmOpen(false);
        }}
        slotProps={{
          paper: {
            "aria-describedby": "contract-confirm-description",
            "aria-labelledby": "contract-confirm-title",
            role: "alertdialog",
          },
        }}
      >
        <DialogTitle id="contract-confirm-title">
          Accept the current agreement?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="contract-confirm-description">
            Your acceptance applies to {versionLabel(terms.version_number)} at
            a {feeDisplay} commission rate and the exact document whose SHA-256
            ends in {terms.document?.content_sha256.slice(-12)}. The retained
            copy cannot be replaced by a future publication.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            disabled={acceptMutation.isPending}
            onClick={() => setConfirmOpen(false)}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={acceptMutation.isPending || !canAcceptExactAgreement}
            onClick={() => {
              if (acceptMutation.isPending || !terms.version_id || !terms.document) return;
              acceptMutation.mutate({
                versionId: terms.version_id,
                documentSha256: terms.document.content_sha256,
              });
            }}
            size="small"
            variant="contained"
          >
            {acceptMutation.isPending ? "Confirming…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
